/**
 * Shared routing helpers for the two portals (admin / client).
 *
 * Session detection reads localStorage directly instead of the auth services so
 * it stays usable from `redirectTo` functions, which run outside an injection
 * context.
 */

export type Portal = 'admin' | 'client';

const TOKEN_KEYS: Record<Portal, string> = {
  admin: 'auth_token',
  client: 'client_auth_token',
};

export const PORTAL_HOME: Record<Portal, string> = {
  admin: '/admin/dashboard',
  client: '/client/projects',
};

export function hasSession(portal: Portal): boolean {
  if (typeof localStorage === 'undefined') return false;
  return !!localStorage.getItem(TOKEN_KEYS[portal]);
}

/**
 * Where the app lands when no path is given.
 *
 * The client portal is the default: it is the larger audience and the only one
 * that needs a discoverable entry point, while admins reach `/admin` through
 * bookmarks and domain-restricted Google Workspace sign-in.
 *
 * When both sessions coexist (a Delta engineer with a client test account) the
 * admin session wins — it is the primary workspace, and the client portal is
 * one navigation away.
 */
export function resolveRootRedirect(): string {
  if (hasSession('admin')) return PORTAL_HOME.admin;
  if (hasSession('client')) return PORTAL_HOME.client;
  return '/client/login';
}

/**
 * Validates a `returnUrl` query param before navigating to it.
 *
 * Guards against open redirect and cross-portal jumps: the value must be a
 * relative path inside the portal that issued it. Anything else falls back to
 * that portal's home.
 */
export function sanitizeReturnUrl(raw: string | null, portal: Portal): string {
  const home = PORTAL_HOME[portal];
  if (!raw) return home;

  // Reject absolute URLs, protocol-relative URLs and backslash variants that
  // browsers normalize into an authority.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return home;
  if (raw.includes('://')) return home;

  const path = raw.split(/[?#]/)[0];
  if (path !== `/${portal}` && !path.startsWith(`/${portal}/`)) return home;

  // Never bounce back into an auth screen.
  if (/\/(login|forgot-password|reset-password)(\/|$)/.test(path)) return home;

  return raw;
}
